import {
  DECAY_DEFAULT_S,
  DEFAULT_FIELD_FADE,
  DEFAULT_PITCH_INDEX,
  DELAY_DEFAULT_S,
  ROUTER_HOLD_DEFAULT_S,
  LAMP_DEFAULT_HUE,
  LAMP_DEFAULT_SAT,
  PITCH_INDEX_MAX,
  KNOB_DEFAULT_VALUE,
  KNOB_MIN_DEFAULT,
  KNOB_MAX_DEFAULT,
} from "./constants";
import { scaleNeighbor, snapToScale } from "./audio";
import { defaultEmitterParams } from "./emitters";
import { cloneOrbitParams, defaultOrbitParams, defaultParams, defaultSeq, ObjectType } from "./objects";
import { allowedPitchClasses } from "./pitchScale";
import { AppContext, Tool } from "./appContext";

const SIMPLE_STREAM_RATE = 10000 / 60; // per-second (= 10000/min)
const SIMPLE_STREAM_SPREAD_DEG = 5;
const SIMPLE_SEQ_SPREAD_DEG = 0;
const SIMPLE_EMITTER_SPEED = 10;
const SIMPLE_EMITTER_SPEED_RANGE = 3;
const SIMPLE_EMITTER_LIFE_S = 10;
const SIMPLE_EMITTER_LIFE_RANGE_S = 1;

interface ToolSpawnDeps {
  getActiveTool(): Tool | null;
  setActiveToolState(tool: Tool | null): void;
  highlightTool(tool: Tool | null): void;
  persist(): void;
  bindPanel(): void;
  showArmedPreview(): void;
  hideArmedPreview(): void;
}

export interface ToolSpawnApi {
  advanceSpawnPitchCycle(): void;
  resetObjectSpawn(type: ObjectType): void;
  resetEmitterSpawn(tool: "emitter-sync" | "emitter-full"): void;
  setActiveTool(tool: Tool | null): void;
  disarmTool(): void;
}

export function isEmitterTool(tool: Tool): tool is "emitter-sync" | "emitter-full" {
  return tool === "emitter-sync" || tool === "emitter-full";
}

/**
 * Tool arming and spawn-template lifecycle: arming/disarming the active tool
 * and resetting the object / emitter spawn templates from the last-touched
 * peer (with the spawn-pitch cycle for sounding bodies).
 */
export function initToolSpawn(ctx: AppContext, deps: ToolSpawnDeps): ToolSpawnApi {
  const { field, emitterField, graph, settings, spawn, emitterSpawn } = ctx;

  let pitchCycleStep = 0;

  /**
   * Pitch index for the next placed sounding body, honoring the spawn timbre's
   * allowed pitch-class set (simple mode → pentatonic, else the wired
   * PitchQuantize mask, else chromatic — the same resolution the ◀▶ buttons
   * use). Within a scale it steps one degree above the last peer, snapping the
   * default pitch into the scale when none exists. Chromatic steps one semitone
   * above the last peer (dropping an octave when that would exceed the pitch
   * clamp), falling back to cycling one octave above the default pitch.
   */
  function nextSpawnPitch(): number {
    const last = field.lastSelectedOfType(spawn.type);
    const classes = allowedPitchClasses(spawn.timbreId, settings.simpleMode, field, graph);
    if (classes !== null) {
      return last !== null ? scaleNeighbor(last.pitchIndex, 1, classes) : snapToScale(DEFAULT_PITCH_INDEX, classes);
    }
    if (last !== null) {
      const next = last.pitchIndex + 1;
      return next > PITCH_INDEX_MAX ? next - 12 : next;
    }
    return DEFAULT_PITCH_INDEX + (pitchCycleStep % 12);
  }

  function isPitchedType(type: ObjectType): boolean {
    return type === "box" || type === "sphere" || type === "funnel";
  }

  function advanceSpawnPitchCycle(): void {
    if (!isPitchedType(spawn.type)) {
      return;
    }
    pitchCycleStep += 1;
    spawn.pitchIndex = nextSpawnPitch();
  }

  /**
   * Resets the object spawn template for `type`. If the same type was previously
   * selected, copies that object's live parameters (restitution, friction, mass,
   * timbreId, volume, fade, operator, operand, params, orbit, rotation,
   * railTimbre, massEnabled, killOnContact, collisionEnabled) so the next placement
   * mirrors the last-touched peer. Falls back to hardcoded defaults when no prior
   * selection exists for the type or the remembered object has been deleted.
   * pitchIndex continues one semitone above the last-touched peer (octave cycle
   * when none); railPoints always start empty regardless of the source.
   */
  function resetObjectSpawn(type: ObjectType): void {
    spawn.type = type;
    const last = field.lastSelectedOfType(type);
    if (last !== null) {
      spawn.restitution = last.restitution;
      spawn.friction = last.friction;
      spawn.mass = last.mass;
      spawn.massEnabled = last.massEnabled;
      spawn.killOnContact = last.killOnContact;
      spawn.collisionEnabled = last.collisionEnabled;
      spawn.rotation.copy(last.rotation);
      spawn.timbreId = last.timbreId;
      spawn.volume = last.volume;
      spawn.fade = last.fade;
      spawn.operator = last.operator;
      spawn.operand = last.operand;
      spawn.decayS = last.decayS;
      spawn.timerMode = last.timerMode;
      spawn.knobValue = last.knobValue;
      spawn.knobMin = last.knobMin;
      spawn.knobMax = last.knobMax;
      spawn.knobInt = last.knobInt;
      spawn.buttonMode = last.buttonMode;
      spawn.lampHue = last.lampHue;
      spawn.lampSat = last.lampSat;
      spawn.lampValue = last.lampValue;
      spawn.seqMin = last.seqMin;
      spawn.seqMax = last.seqMax;
      spawn.seqLen = last.seqLen;
      spawn.seqValues = [...last.seqValues];
      spawn.params = last.params.clone();
      spawn.arcDeg = last.arcDeg;
      spawn.orbit = cloneOrbitParams(last.orbit);
      spawn.railTimbre = last.railTimbre;
    } else {
      spawn.restitution = type === "rail" ? 0 : 1;
      spawn.friction = type === "rail" ? 0 : 1;
      spawn.mass = settings.mass;
      spawn.massEnabled = false;
      spawn.killOnContact = false;
      spawn.collisionEnabled = true;
      spawn.rotation.identity();
      spawn.timbreId = "bell";
      spawn.volume = 1;
      spawn.fade = DEFAULT_FIELD_FADE;
      spawn.operator = "add";
      spawn.operand = 0;
      spawn.decayS = DECAY_DEFAULT_S;
      spawn.delayS = DELAY_DEFAULT_S;
      spawn.routerHoldS = ROUTER_HOLD_DEFAULT_S;
      spawn.routerHoldResetOnPulse = false;
      spawn.timerMode = "loop";
      spawn.knobValue = KNOB_DEFAULT_VALUE;
      spawn.knobMin = KNOB_MIN_DEFAULT;
      spawn.knobMax = KNOB_MAX_DEFAULT;
      spawn.knobInt = false;
      spawn.buttonMode = "push";
      spawn.lampHue = LAMP_DEFAULT_HUE;
      spawn.lampSat = LAMP_DEFAULT_SAT;
      spawn.lampValue = 0;
      const seq = defaultSeq(type);
      spawn.seqMin = seq.seqMin;
      spawn.seqMax = seq.seqMax;
      spawn.seqLen = seq.seqLen;
      spawn.seqValues = seq.seqValues;
      spawn.params = defaultParams(type);
      spawn.arcDeg = 360;
      spawn.orbit = defaultOrbitParams();
      spawn.railTimbre = "sine";
    }
    spawn.pitchIndex = isPitchedType(type) ? nextSpawnPitch() : DEFAULT_PITCH_INDEX;
    spawn.pitchIndexEnd = spawn.pitchIndex + 12;
    spawn.railPoints = [];
  }

  /**
   * Resets the emitter spawn template for `tool`. If an emitter of the matching
   * kind was previously selected, copies its live parameters so the next
   * placement mirrors the last-touched peer of the same kind. Falls back to
   * hardcoded defaults when no prior selection exists or the remembered emitter
   * has been deleted. name always resets to ""; configureEmitterSpawn normalises
   * kind-specific state (clockSync, seqMode, speed/life collapse for sync) in
   * both paths.
   */
  function resetEmitterSpawn(tool: "emitter-sync" | "emitter-full"): void {
    const kind: "sync" | "full" = tool === "emitter-sync" ? "sync" : "full";
    const last = emitterField.lastSelectedOfKind(kind);
    if (last !== null) {
      emitterSpawn.rate = last.rate;
      emitterSpawn.rotation.copy(last.rotation);
      emitterSpawn.coneDeg = last.coneDeg;
      emitterSpawn.speedMin = last.speedMin;
      emitterSpawn.speedMax = last.speedMax;
      emitterSpawn.lifeMin = last.lifeMin;
      emitterSpawn.lifeMax = last.lifeMax;
      emitterSpawn.offset = last.offset;
      emitterSpawn.bounce = last.bounce;
      emitterSpawn.friction = last.friction;
      emitterSpawn.drag = last.drag;
      emitterSpawn.meter = last.meter;
      emitterSpawn.num = last.num;
      emitterSpawn.seqSteps = [...last.seqSteps];
      emitterSpawn.pulseSpeedFromSettings = last.pulseSpeedFromSettings;
    } else {
      const d = defaultEmitterParams();
      emitterSpawn.rate = tool === "emitter-full" ? SIMPLE_STREAM_RATE : d.rate;
      emitterSpawn.rotation.copy(d.rotation);
      emitterSpawn.coneDeg = tool === "emitter-sync" ? SIMPLE_SEQ_SPREAD_DEG : SIMPLE_STREAM_SPREAD_DEG;
      emitterSpawn.speedMin = SIMPLE_EMITTER_SPEED - SIMPLE_EMITTER_SPEED_RANGE / 2;
      emitterSpawn.speedMax = SIMPLE_EMITTER_SPEED + SIMPLE_EMITTER_SPEED_RANGE / 2;
      emitterSpawn.lifeMin = SIMPLE_EMITTER_LIFE_S - SIMPLE_EMITTER_LIFE_RANGE_S / 2;
      emitterSpawn.lifeMax = SIMPLE_EMITTER_LIFE_S + SIMPLE_EMITTER_LIFE_RANGE_S / 2;
      emitterSpawn.offset = d.offset;
      emitterSpawn.bounce = d.bounce;
      emitterSpawn.friction = d.friction;
      emitterSpawn.drag = d.drag;
      emitterSpawn.meter = d.meter;
      emitterSpawn.num = d.num;
      emitterSpawn.seqSteps = [];
      emitterSpawn.pulseSpeedFromSettings = d.pulseSpeedFromSettings;
    }
    emitterSpawn.name = "";
    configureEmitterSpawn(kind);
  }

  function configureEmitterSpawn(kind: "sync" | "full"): void {
    emitterSpawn.kind = kind;
    if (kind === "sync") {
      emitterSpawn.clockSync = true;
      emitterSpawn.seqMode = true;
      const speedMid = (emitterSpawn.speedMin + emitterSpawn.speedMax) / 2;
      emitterSpawn.speedMin = speedMid;
      emitterSpawn.speedMax = speedMid;
      const lifeMid = (emitterSpawn.lifeMin + emitterSpawn.lifeMax) / 2;
      emitterSpawn.lifeMin = lifeMid;
      emitterSpawn.lifeMax = lifeMid;
      if (emitterSpawn.seqSteps.length === 0) {
        const quarters: number[] = [];
        for (let step = 0; step < emitterSpawn.num; step += 4) {
          quarters.push(step);
        }
        emitterSpawn.seqSteps = quarters;
      }
    } else {
      emitterSpawn.clockSync = false;
      emitterSpawn.seqMode = false;
    }
  }

  function setActiveTool(tool: Tool | null): void {
    deps.setActiveToolState(tool);
    field.deselect();
    emitterField.deselect();
    if (tool && isEmitterTool(tool)) {
      resetEmitterSpawn(tool);
    } else if (tool) {
      resetObjectSpawn(tool);
      settings.shape = tool;
      deps.persist();
    }
    deps.highlightTool(tool);
    deps.bindPanel();
    deps.showArmedPreview();
  }

  /**
   * Clears the armed tool without touching the current selection, so confirming
   * a selection can stay mutually exclusive with an armed tool (arming a tool
   * already deselects via setActiveTool; this is the reverse direction).
   */
  function disarmTool(): void {
    if (deps.getActiveTool() === null) {
      return;
    }
    deps.setActiveToolState(null);
    deps.highlightTool(null);
    deps.hideArmedPreview();
  }

  return { advanceSpawnPitchCycle, resetObjectSpawn, resetEmitterSpawn, setActiveTool, disarmTool };
}
